// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Severity tiers, most severe first.
 *
 * ## Sprint 64.57.0 gate E12.5 pass 2, finding F4 — one ordering, not three
 *
 * This order was written out three times: `SEVERITY_ORDER` in the backend
 * registry, `severityRank`'s `switch` in the completeness service, and a second
 * `SEVERITY_ORDER` in the React page. The page's copy existed because the field
 * it needed — which tiers the report evaluated — was not on this type, so the
 * screen re-derived it from a boolean and could not be corrected by fixing the
 * backend. It lives here because that is the one place both sides can read.
 */
export const COMPLETENESS_SEVERITY_ORDER = [
    'critical',
    'high',
    'conditional',
    'informational',
];
/** List of all supported framework codes — used for validation. */
export const ROPA_COMPLETENESS_FRAMEWORKS = [
    'PDPL',
    'GDPR',
    'DECREE_165',
    'ISO_27701',
];
/**
 * fix-sprint-20260506-2121-followup L5 — the report fields the RoPA autofix can
 * draft a value for. One list: the backend's `RopaFixableField` and the
 * frontend's "show Ask Forseti on this field" check both derive from it, where
 * each had written the nine names out.
 */
export const ROPA_AUTOFIX_FIELDS = [
    'description',
    'purpose',
    'lawful_basis',
    'data_subjects',
    'data_categories',
    'recipients',
    'cross_border_transfers',
    'retention_period',
    'security_measures',
];
/** Is this report field one the autofix can draft? */
export function isRopaAutofixField(field) {
    return ROPA_AUTOFIX_FIELDS.includes(field);
}
/**
 * A family's wire spelling.
 *
 * The single cast in this round trip, kept here beside the type it produces:
 * TypeScript cannot prove that `String.replace` implements `DashSeparated`, and
 * every caller that needs the transform would otherwise carry its own cast —
 * which is what `AskForsetiFix.tsx` did while its docblock claimed the derived
 * type made a mismatch impossible.
 */
export function ropaCompletenessFrameworkSlug(family) {
    return family.toLowerCase().replace(/_/g, '-');
}
/**
 * Sprint 46.0.1 — map the registry's short codes to the canonical
 * `framework_code` values stored in `grc_regulatory_frameworks`. Used by the
 * installed-frameworks filter so the picker only surfaces frameworks the
 * tenant has actually installed via the localization pack-install-engine.
 *
 * ## Sprint 64.57.0 — this is an ALIAS table, not the availability mechanism
 *
 * It used to be both, and that is the whole of idea-catalog **#113**. A family
 * was available iff this table named an installed code, so a family whose name
 * is *not* an alias of anything — `ISO_27701` — was available to nobody, and
 * the comment that used to sit here pointed at `ALWAYS_AVAILABLE_FRAMEWORKS` as
 * the mechanism that covered it. That list had been `[]` since the Sprint 45.4.0
 * hotfix emptied it (correctly: it force-listed the family to tenants who had
 * not installed it), so the pointer had been false for eleven sprints and the
 * family was declared, labelled in sixteen locales, rendered in the picker's
 * preference order, given an export branch and six rules — and returned to
 * nobody.
 *
 * The mechanism is now a derivation, in `ropa-family-availability.ts`: a family
 * is available when the organisation installed an instrument that **belongs to
 * it**, decided by matching the family's own identifier against the installed
 * code, exactly as `filterFrameworkRelevanceToActive` already decides family
 * membership for `framework_relevance`. `ISO_27701` therefore needs no entry
 * here and gets none — an installed `ISO_27701_2019` resolves to it by name.
 *
 * What survives here is the residue that no derivation can produce: the codes
 * whose instrument name does not contain the family's name. `EU_GDPR` is not
 * spelled `GDPR…`; `VN_PDPL_LAW_2025` is not spelled `PDPL…`. Those are
 * aliases, they are ordered current-instrument-first, and that order is also
 * the citation preference order. Adding a family here to make it selectable is
 * the defect restated — install the pack instead.
 *
 * ## Sprint 64.8.0 — one code per family was one code too few
 *
 * `PDPL` mapped to a single code, and it was the **legacy** one: that framework
 * carries `status: 'legacy'` and a `replaced_by` pointing at the successor law.
 * A tenant installed on the current law and its implementing decree — the
 * shipping configuration — therefore had **no PDPL option in the picker at
 * all**, and no completeness report for the regime they are actually subject
 * to. The same mis-pinning shipped in the incident SLA seeder and was fixed in
 * this sprint's first half; this is the second instance, found by auditing
 * `status` across the corpus rather than by anybody reporting it.
 *
 * A family is now a **list**, ordered current-first. Installing any member
 * makes the family available, so the legacy tenant keeps the report it had and
 * the current-law tenant gains the one it should always have had. The list is
 * ordered because the first installed member is also the framework whose
 * requirements are cited first.
 */
export const FRAMEWORK_CODE_MAP = {
    PDPL: ['VN_PDPL_DECREE_356_2025', 'VN_PDPL_LAW_2025', 'VN_PDPD_DECREE_13'],
    GDPR: ['EU_GDPR', 'UK_GDPR'],
    DECREE_165: ['VN_DATA_LAW_DECREE_165_2025'],
};
/*
 * `ALWAYS_AVAILABLE_FRAMEWORKS` was removed in Sprint 64.57.0.
 *
 * It was the picker's install-gate bypass. Sprint 45.4.0 hotfix-3 emptied it
 * because ISO 27701 was being force-listed to demo tenants (Saigon Retail Ltd)
 * that had never installed it, and left this note behind:
 *
 *   "The picker is now strictly install-gated: ISO 27701 only appears for
 *    tenants that have explicitly opted in via their localization pack."
 *
 * That sentence described a mechanism the code did not have — there was no way
 * for any tenant to opt in, because nothing joined an installed instrument to
 * that family. Sprint 64.57.0 built the join (`ropa-family-availability.ts`)
 * and deleted the bypass rather than refilling it: an empty list whose only
 * possible future use is to re-force-list a family is the 45.4.0 defect kept
 * warm. Every family now earns its place in the picker the same way, and
 * `getInstalledRegulatoryFrameworks` returning `[]` for a tenant with no
 * installed packs is pinned by test.
 */
//# sourceMappingURL=ropa-completeness.js.map